import classNames from "classnames";
import * as React from "react";
import { RefObject, useMemo } from "react";
import { useCustomization } from "~/Redux/Customization";
import { commonClient as commonClientResxEn } from "~/resxDist/commonClientResx.en";
import { Icon } from "~/sharedComponents/icons";
import { riskEntityThumbnailData } from "~/sharedComponents/thumbnails/consts";
import { Qtip } from "~/sharedComponents/tooltip";
import { colorOfContentMap } from "~/sharedHOC/colorPicker/const";
import { Center } from "../layout/Flex";
import * as styles from "./EntityThumbnail.less";
import { ThumbnailData } from "./types";

type Props = ThumbnailProps & { item: ThumbnailData; showTooltip?: boolean };

export const EntityThumbnail: React.FC<Props> = ({ item, showTooltip = true, ...boxProps }) => {
    let { color, background, itemType, itemIcon, tooltip, size = "small" } = item;
    let typeAbbreviation = "";

    const isRename = useCustomization(x => x?.IsRenameIssueToRisk, { loadCustomization: false });

    if (isRename && itemType === riskEntityThumbnailData.itemType) {
        ({ itemIcon, color, background, tooltip } = riskEntityThumbnailData);
    }

    if (!itemIcon) {
        const label = commonClientResxEn[`Entity_${itemType}` as keyof typeof commonClientResxEn];

        if (label) {
            typeAbbreviation = label.substring(0, 1);
        }
    }

    return (
        <Qtip hide={{ delay: 100 }} isVisible={showTooltip}>
            <Thumbnail background={background} color={color} data-testid="entity-thumbnail" {...boxProps}>
                {itemIcon ? <Icon iconName={itemIcon} size={size} /> : typeAbbreviation}
            </Thumbnail>
            {tooltip}
        </Qtip>
    );
};

export const entityThumbnailMargin = 2; // (ExpandCollapseButtonWidth - 20px)/2;
/**
 * In grids we need to add margins to the thumbnail to align it with the expand/collapse button.
 * The overall width must be 24px, see ExpandCollapseButtonWidth.
 */
export const EntityThumbnailForGrid: React.FC<Omit<Props, "marginLeft" | "marginRight">> = props => (
    <EntityThumbnail {...props} marginLeft={entityThumbnailMargin} marginRight={entityThumbnailMargin} />
);

type ThumbnailProps = {
    /** Renders icon-sized thumbnail */
    small?: boolean;
} & React.ComponentProps<typeof Center>;

/** Renders children in container 20x20 or icon-sized 16x16 */
export const Thumbnail: React.FC<ThumbnailProps> = React.forwardRef(
    ({ small, children, className, ...boxProps }, ref: RefObject<HTMLDivElement>) => {
        const color = useMemo(
            () => (boxProps.color ?? boxProps.background ? colorOfContentMap[boxProps.background] : undefined),
            [boxProps.color, boxProps.background]
        );

        return (
            <Center
                className={classNames(styles.thumbnail, small && styles.small, className)}
                {...boxProps}
                color={color}
                ref={ref}
            >
                {children}
            </Center>
        );
    }
);
